import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { ProductSpecificationsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { ProductSpecificationFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { DeleteDialog } from '../delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { useState } from 'react'

interface Props extends TableProps {
  filters?: ProductSpecificationFilters
  createable?: boolean
  deleteable?: boolean
}

export const Table = ({ pageSize = 10, filters = {}, createable = false, deleteable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<ProductSpecificationsQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openDelete,
    setOpenDelete,
  } = useTable({ pageInfo: data?.productSpecifications.pageInfo })
  const [productSpecificationId, setProductSpecificationId] = useState<string>()

  const deleteProductSpecification = (id: string) => {
    if (!deleteable) return

    setProductSpecificationId(id)
    setOpenDelete(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<ProductSpecificationFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Name' }, { name: 'Value' }, { name: '' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.productSpecifications.nodes = dedupe([
              ...previousData.productSpecifications.nodes,
              ...fetchMoreResult.productSpecifications.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        rows={data.productSpecifications.nodes.map(ps => (
          {
            id: ps.id,
            columns: [
              ps.specification.name,
              ps.value,
              deleteable
                ? <div className="text-right -mx-1">
                  <Button color="red" onClick={() => deleteProductSpecification(ps.id) }>Delete</Button>
                </div>
                : <></>
            ]
          }
        ))}
      />
      { filters.productIdEq && filters.productTypeEq && <NewDialog
        defaultValues={ { productId: filters.productIdEq, productType: filters.productTypeEq } }
        open={openNew}
        setOpen={setOpenNew}
      /> }
      { productSpecificationId && (
        <DeleteDialog
          id={productSpecificationId}
          mutation={DeleteMutation}
          open={openDelete}
          refetchQueries={[Query]}
          setOpen={setOpenDelete}
          onSuccess={
            () => {
              setOpenDelete(false)
              setProductSpecificationId(undefined)
            }
          }
        />)}
    </>
  )
}
